import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Icon
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

interface SessionExpiredDialogProps {
  open: boolean;
  onRelogin: () => void;
  onRetry?: () => void;
  message?: string;
  isExpired?: boolean;
  isNetworkError?: boolean;
}

const SessionExpiredDialog: React.FC<SessionExpiredDialogProps> = ({
  open,
  onRelogin,
  onRetry,
  message,
  isExpired = false,
  isNetworkError = false
}) => {
  const getIcon = () => {
    if (isNetworkError) {
      return <Icon>wifi_off</Icon>;
    }
    if (isExpired) {
      return <AccessTimeIcon />;
    }
    return <WarningIcon />;
  };

  const getTitle = () => {
    if (isNetworkError) {
      return '网络连接异常';
    }
    if (isExpired) {
      return '会话已过期';
    }
    return '认证失败';
  };

  const getDefaultMessage = () => {
    if (isNetworkError) {
      return '网络连接异常，请检查网络连接后重试。';
    }
    if (isExpired) {
      return '您的会话已过期（超过7天未使用）。为了保护您的账户安全，请重新登录以继续使用系统。';
    }
    return '会话状态异常，请重新登录以继续使用系统。';
  };

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 3
        }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
          <Box 
            color={isNetworkError ? 'warning.main' : isExpired ? 'info.main' : 'error.main'}
            sx={{ fontSize: 48 }}
          >
            {getIcon()}
          </Box>
        </Box>
        <Typography variant="h6" component="div">
          {getTitle()}
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ textAlign: 'center', py: 2 }}>
        <Typography variant="body1" color="text.secondary" paragraph>
          {message || getDefaultMessage()}
        </Typography>
        
        {isExpired && (
          <Box 
            sx={{ 
              bgcolor: 'info.light', 
              color: 'info.contrastText',
              p: 2, 
              borderRadius: 1,
              mt: 2
            }}
          >
            <Typography variant="body2">
              💡 提示：为了保护您的账户安全，系统会在7天无活动后自动退出登录。
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        {onRetry && (
          <Button
            onClick={onRetry}
            variant="outlined"
            sx={{ minWidth: 100 }}
          >
            稍后重试
          </Button>
        )}
        <Button
          onClick={onRelogin}
          variant="contained"
          color="primary"
          sx={{ minWidth: 100 }}
        >
          重新登录
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionExpiredDialog; 